/**
 * AddComparisonSourceModal
 *
 * Picks the other video for a side-by-side comparison in the clip recorder.
 *
 * Two screens: a list of the six sources, and one screen per source. Choosing
 * a row is the whole interaction — it hands the source back and closes. There
 * is no confirm step, because the one that existed only re-sent a selection
 * that was already complete.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type { ClipSource } from './ClipEditorModal';
import type { NotableSegment, SavedPlayerSegment } from '../types/videoEdit';
import type { Drill, ProVideo } from '../types/lesson';
import { PlayerSegmentLibrary } from './PlayerSegmentLibrary';
import { SourceListRow, BrowseRow } from './videoEditor/pickerRows';
import { rangeLabel, rowName } from './videoEditor/rowText';
import { RowThumbnail } from './videoEditor/rowThumbnail';
import { getOriginalVideo, getOriginalVideoUrl } from '../services/videoEditDraft';
import { getStockDrills } from '../services/drills';
import { getAllProVideosForCoach } from '../services/proVideos';
import { useAuth } from '../contexts/AuthContext';
import {
  EBTN_PRIMARY,
  EBTN_ICON,
} from './videoEditor/editorButtonStyles';

// ── Types ──────────────────────────────────────────────────────────────────────

interface AddComparisonSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the chosen comparison source */
  onConfirm: (source: ClipSource) => void;
  /** The already-loaded primary source — displayed read-only */
  primarySource: ClipSource;
  /** Lesson video URL (for "Current Video" tab) */
  videoUrl?: string;
  /** Highlights from the current video */
  notableSegments: NotableSegment[];
  playerId?: string;
  originalVideoId?: string;
  /** Which tab to open first (defaults to 'current', or first available if 'current' is empty) */
  defaultTab?: Tab;
  /** Rotation of the current lesson video — propagated to "Current Video" secondary sources */
  videoRotation?: number;
}

type Tab = 'current' | 'player' | 'local' | 'camera' | 'drill-library' | 'pro-library';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AddComparisonSourceModal({
  isOpen,
  onClose,
  onConfirm,
  primarySource,
  videoUrl = '',
  notableSegments,
  playerId,
  originalVideoId,
  defaultTab,
  videoRotation,
}: AddComparisonSourceModalProps) {
  // Segments available in the "Current Video" tab: exclude external sources and the primary segment itself
  const currentVideoSegments = notableSegments.filter(
    (s) => !s.externalSource && s.id !== primarySource.segment?.id,
  );
  const showCurrentTab = currentVideoSegments.length > 0;

  // Resolve to first available tab: if the preferred tab is 'current' but that tab is hidden,
  // fall through to the next available tab regardless of whether defaultTab was explicit or not.
  const resolveTab = (preferred: Tab | undefined): Tab => {
    const t = preferred ?? 'current';
    if (t === 'current' && !showCurrentTab) return playerId ? 'player' : 'local';
    return t;
  };
  const { currentUser } = useAuth();
  const [tab, setTab] = useState<Tab>(() => resolveTab(defaultTab));
  /**
   * Two views, not six tabs. Opened from the rail with a defaultTab the modal
   * starts on that source; opened cold it starts on the list. Back always
   * returns to the list, so arriving in the wrong place is recoverable.
   */
  const [view, setView] = useState<'list' | 'browse'>(
    /* 'local' has no screen any more — it fires the OS picker from its row — so
       land on the list if resolveTab fell through to it. */
    defaultTab && resolveTab(defaultTab) !== 'local' ? 'browse' : 'list',
  );

  /* The six sources, in the rail menu's order. Same icons as SideBySidePopover
     for the four it already had; academic-cap and trophy for the two it did
     not, both Heroicons outline 24 at strokeWidth 1.5 like the rest. */
  const SOURCES: { tab: Tab; label: string; icon: React.ReactNode; available: boolean; note?: string }[] = [
    { tab: 'camera', label: 'Record with Camera', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" /></svg>, available: true },
    { tab: 'local', label: 'Upload from Device', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>, available: true },
    { tab: 'current', label: 'From This Lesson', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-2.625 0V5.625m0 0A1.125 1.125 0 014.5 4.5h15a1.125 1.125 0 011.125 1.125m-17.25 0v1.5c0 .621.504 1.125 1.125 1.125M19.5 4.5v1.5m0 0a1.125 1.125 0 01-1.125 1.125M19.5 6v1.5m0 9V9.375M6 6.75h.008v.008H6V6.75zm0 3h.008v.008H6V9.75zm0 3h.008v.008H6v-.008zm0 3h.008v.008H6v-.008zm12-9h.008v.008H18V6.75zm0 3h.008v.008H18V9.75zm0 3h.008v.008H18v-.008z" /></svg>, available: showCurrentTab, note: 'None' },
    { tab: 'player', label: 'From Previous Lesson', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, available: !!playerId, note: 'N/A' },
    { tab: 'drill-library', label: 'Drill Library', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.636 50.636 0 00-2.658-.813A59.906 59.906 0 0112 3.493a59.903 59.903 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" /></svg>, available: true },
    { tab: 'pro-library', label: 'Pro Library', icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" /></svg>, available: true },
  ];
  const [loading, setLoading] = useState(false);


  // Drill library
  const [drills, setDrills] = useState<Drill[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillLoaded, setDrillLoaded] = useState(false);
  const [drillPreviewing, setDrillPreviewing] = useState<string | null>(null);

  // Pro library (stock + coach's own)
  const [stockProVideos, setStockProVideos] = useState<ProVideo[]>([]);
  const [coachProVideos, setCoachProVideos] = useState<ProVideo[]>([]);
  const [proLoading, setProLoading] = useState(false);
  const [proLoaded, setProLoaded] = useState(false);
  const [proPreviewing, setProPreviewing] = useState<string | null>(null);

  // Current video previewing
  const [previewing, setPreviewing] = useState<Record<string, boolean>>({});

  // Local file
  /** Fires the OS picker straight from the source row — no screen in between. */
  const localFileInputRef = useRef<HTMLInputElement>(null);
  /**
   * The list sources select first and open second, the Apple picker pattern:
   * a tap is reversible, and the coach sees what they chose before committing.
   * Camera and Upload keep their own single action — neither has a list to
   * choose within, so there is nothing to reconsider.
   */
  const [selected, setSelected] = useState<ClipSource | null>(null);

  // Camera
  const cameraRef = useRef<HTMLVideoElement>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setTab(resolveTab(defaultTab));
    setSelected(null);
    setPreviewing({});
    setDrillLoaded(false);
    setDrillPreviewing(null);
    setProLoaded(false);
    setProPreviewing(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Load web-optimized drills when drill library tab is first opened
  useEffect(() => {
    if (tab !== 'drill-library' || drillLoaded) return;
    setDrillLoading(true);
    getStockDrills()
      .then((all) => setDrills(all.filter((d) => !!d.webOptimizedVideoUrl)))
      .catch(() => {})
      .finally(() => { setDrillLoading(false); setDrillLoaded(true); });
  }, [tab, drillLoaded]);

  // Load pro videos when pro library tab is first opened
  useEffect(() => {
    if (tab !== 'pro-library' || proLoaded || !currentUser) return;
    setProLoading(true);
    getAllProVideosForCoach(currentUser.uid)
      .then(({ stock, coach }) => {
        // Only show stock videos that have a usable URL; coach videos show all (even without web-optimized)
        setStockProVideos(stock.filter((v) => !!v.webOptimizedVideoUrl || (!!v.videoUrl && !v.videoUrl.startsWith('https://placeholder.'))));
        setCoachProVideos(coach.filter((v) => !!v.videoUrl && !v.videoUrl.startsWith('https://placeholder.')));
      })
      .catch(() => {})
      .finally(() => { setProLoading(false); setProLoaded(true); });
  }, [tab, proLoaded, currentUser]);

  // Camera stream lifecycle
  useEffect(() => {
    if (!isOpen || tab !== 'camera') {
      cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
      return;
    }
    setCameraError(null);
    navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      .then((stream) => {
        cameraStreamRef.current = stream;
        if (cameraRef.current) cameraRef.current.srcObject = stream;
      })
      .catch(() => setCameraError('Camera access denied or unavailable.'));
    return () => {
      cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
    };
  }, [isOpen, tab]);

  // Stop camera when modal closes
  useEffect(() => {
    if (!isOpen) {
      cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
    }
  }, [isOpen]);

  /**
   * Choosing a source is the whole interaction: hand it back and close.
   *
   * There was a confirm step, and it did nothing a selection had not already
   * done — `handleConfirm` was `onConfirm(selected); onClose()`. Every source
   * builds its complete ClipSource at selection time, the local file's
   * `URL.createObjectURL` included, so nothing was deferred to it.
   */
  const commit = useCallback((source: ClipSource) => {
    onConfirm(source);
    onClose();
  }, [onConfirm, onClose]);

  const handleSelectFromPlayerLibrary = useCallback(async (seg: SavedPlayerSegment) => {
    setLoading(true);
    try {
      const originalVideo = await getOriginalVideo(seg.context, seg.originalVideoId);
      if (!originalVideo) throw new Error('Video not found');
      const segVideoUrl = await getOriginalVideoUrl(originalVideo);
      const syntheticSeg: NotableSegment = {
        id: seg.id,
        label: seg.label,
        note: seg.note,
        startTime: seg.startTime,
        endTime: seg.endTime,
        externalSource: 'player_library',
      };
      setSelected({ videoUrl: segVideoUrl, segment: syntheticSeg, label: seg.label });
    } catch {
      // silently ignore — user can retry
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectFromDrillLibrary = useCallback((drill: Drill) => {
    setSelected({ videoUrl: drill.webOptimizedVideoUrl!, label: drill.title });
  }, []);

  const handleSelectFromProLibrary = useCallback((video: ProVideo) => {
    // Prefer web-optimized; fall back to original. URL is captured at selection time
    // so future edits or deletions of the source document cannot affect this recording.
    const url = video.webOptimizedVideoUrl ?? video.videoUrl;
    const label = video.playerName ? `${video.playerName} · ${video.title}` : video.title;
    setSelected({ videoUrl: url, label });
  }, []);


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70">
      <div className="rounded-xl w-full max-w-lg mx-4 shadow-xl flex flex-col max-h-[85vh]" style={{ background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}>

        {/* One nav row. The title, the subhead, the "Select right video"
            divider and the breadcrumb all said the same thing; the source's
            own name says it once. */}
        <div className="flex items-center gap-1 px-3 py-2.5 flex-shrink-0">
          {view === 'browse' ? (
            <button
              type="button"
              onClick={() => setView('list')}
              className={`${EBTN_ICON}`}
              aria-label="Back to sources"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          ) : (
            <span className="w-8 flex-shrink-0" />
          )}
          <h3 className="flex-1 min-w-0 text-center text-base font-semibold text-[var(--editor-text)] truncate">
            {view === 'list' ? 'Compare with' : SOURCES.find((x) => x.tab === tab)?.label}
          </h3>
          <button type="button" onClick={onClose} className={EBTN_ICON} aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <input
          ref={localFileInputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            /* Same construction the organizer's Upload from Device uses:
               a blob URL and the filename minus its extension. */
            commit({ videoUrl: URL.createObjectURL(f), label: f.name.replace(/\.[^.]+$/, '') });
          }}
        />

        {/* ── View 1: the source list ── */}
        {view === 'list' && (
          <div className="flex-1 overflow-y-auto min-h-0 px-2 pb-2">
            {SOURCES.map((src) => (
              <SourceListRow
                key={src.tab}
                label={src.label}
                icon={src.icon}
                available={src.available}
                note={src.note}
                onOpen={() => {
                  if (src.tab === 'local') { localFileInputRef.current?.click(); return; }
                  setTab(src.tab);
                  setView('browse');
                }}
              />
            ))}
          </div>
        )}

        {/* ── View 2: browse one source ── */}
        {view === 'browse' && (
        <>
        {loading && (
          <div className="flex justify-center pb-2 flex-shrink-0">
            <div className="w-4 h-4 border border-[var(--editor-text-muted)] border-t-[var(--editor-text)] rounded-full animate-spin" />
          </div>
        )}

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">

          {/* Current Video */}
          {tab === 'current' && (
            <div className="space-y-1.5">
              {currentVideoSegments.length === 0 && (
                <p className="text-xs text-[var(--editor-text-muted)] px-1 py-8 text-center">No other highlights in this lesson yet.</p>
              )}
              {currentVideoSegments.map((seg, i) => {
                const isPreviewing = !!previewing[seg.id];
                /* Fields a NotableSegment actually has: label, optional note,
                   startTime, endTime. The range is the only thing that reliably
                   separates two segments with the same name, so it always shows. */
                const range = rangeLabel(seg.startTime, seg.endTime);
                return (
                  <BrowseRow
                    key={seg.id}
                    thumb={<RowThumbnail cacheKey={`cur:${seg.id}`} videoUrl={videoUrl} time={seg.startTime} />}
                    primary={rowName(seg.label, i)}
                    secondary={seg.note ? `${seg.note} · ${range}` : range}
                    selected={selected?.segment?.id === seg.id}
                    onSelect={() => setSelected({ videoUrl, segment: seg, label: seg.label, rotation: videoRotation || undefined })}
                    previewing={isPreviewing}
                    onTogglePreview={() => setPreviewing((pv) => ({ ...pv, [seg.id]: !pv[seg.id] }))}
                  >
                    {isPreviewing && (
                      <div className="border-t border-[var(--editor-border)] bg-black">
                        <video
                          src={videoUrl}
                          controls
                          className="w-full max-h-40 object-contain"
                          onLoadedMetadata={(e) => { e.currentTarget.currentTime = seg.startTime; }}
                          onTimeUpdate={(e) => {
                            if (seg.startTime !== seg.endTime && e.currentTarget.currentTime >= seg.endTime) {
                              e.currentTarget.pause();
                              e.currentTarget.currentTime = seg.startTime;
                            }
                          }}
                        />
                      </div>
                    )}
                  </BrowseRow>
                );
              })}
            </div>
          )}

          {/* Player Library */}
          {tab === 'player' && playerId && (
            <div>
              <PlayerSegmentLibrary
                playerId={playerId}
                currentOriginalVideoId={originalVideoId}
                /* Hard scope, not an optional filter. The library holds every
                   segment any coach ever saved for this player; only the ones
                   this coach saved belong in this coach's picker. */
                coachId={currentUser?.uid ?? ''}
                onSelect={(seg) => handleSelectFromPlayerLibrary(seg)}
                selectedSegmentId={selected?.segment?.id}
                onSegmentUpdated={() => {}}
                compact
              />
            </div>
          )}

          {/* Drill Library */}
          {tab === 'drill-library' && (
            <div className="space-y-1.5">
              {drillLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-[var(--editor-text-muted)] border-t-[var(--editor-text)] rounded-full animate-spin" />
                </div>
              )}
              {!drillLoading && drills.length === 0 && (
                <p className="text-xs text-[var(--editor-text-muted)] px-1 py-8 text-center">No drills are ready to use yet.</p>
              )}
              {!drillLoading && drills.map((drill) => {
                const isPreviewing = drillPreviewing === drill.id;
                /* Drill has title, shotType, description and an optional
                   durationSeconds. shotType is what tells two drills apart at
                   a glance; description is a paragraph and belongs in the info
                   modal, not on a row. */
                const secondary = drill.durationSeconds
                  ? `${drill.shotType} · ${formatTime(drill.durationSeconds)}`
                  : drill.shotType;
                return (
                  <BrowseRow
                    key={drill.id}
                    thumb={(
                      <RowThumbnail
                        cacheKey={`drill:${drill.id}`}
                        thumbnailUrl={drill.thumbnailUrl}
                        videoUrl={drill.webOptimizedVideoUrl}
                      />
                    )}
                    primary={drill.title}
                    secondary={secondary}
                    selected={selected?.videoUrl === drill.webOptimizedVideoUrl}
                    onSelect={() => handleSelectFromDrillLibrary(drill)}
                    previewing={isPreviewing}
                    onTogglePreview={() => setDrillPreviewing(isPreviewing ? null : drill.id)}
                  >
                    {isPreviewing && (
                      <div className="border-t border-[var(--editor-border)] bg-black">
                        <video src={drill.webOptimizedVideoUrl} controls className="w-full max-h-40 object-contain" />
                      </div>
                    )}
                  </BrowseRow>
                );
              })}
            </div>
          )}

          {/* Pro Library */}
          {tab === 'pro-library' && (
            <div className="space-y-3">
              {proLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-[var(--editor-text-muted)] border-t-[var(--editor-text)] rounded-full animate-spin" />
                </div>
              )}
              {!proLoading && stockProVideos.length === 0 && coachProVideos.length === 0 && (
                <p className="text-xs text-[var(--editor-text-muted)] px-1 py-8 text-center">No pro videos yet. You can add your own from your Account page.</p>
              )}
              {!proLoading && (
                <>
                  {/* DB Pro Library section */}
                  {stockProVideos.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-[var(--editor-text-muted)] uppercase tracking-wider px-1 mb-1.5">DB Pro Library</p>
                      <div className="space-y-1.5">
                        {stockProVideos.map((video) => {
                          const url = video.webOptimizedVideoUrl ?? video.videoUrl;
                          const isPreviewing = proPreviewing === video.id;
                          /* ProVideo has title, playerName, tags, an optional
                             description and an optional durationSeconds. Title
                             leads; playerName and tags are what separate two
                             videos of the same stroke, so they form the quiet
                             line. Description is a paragraph, not a row. */
                          const secondary = [
                            video.playerName,
                            ...video.tags,
                            video.durationSeconds ? formatTime(video.durationSeconds) : null,
                          ].filter(Boolean).join(' · ');
                          return (
                            <BrowseRow
                              key={video.id}
                              thumb={(
                                <RowThumbnail
                                  cacheKey={`pro:${video.id}`}
                                  thumbnailUrl={video.thumbnailUrl}
                                  videoUrl={url}
                                />
                              )}
                              primary={video.title}
                              secondary={secondary || undefined}
                              selected={selected?.videoUrl === url}
                              onSelect={() => handleSelectFromProLibrary(video)}
                              previewing={isPreviewing}
                              onTogglePreview={() => setProPreviewing(isPreviewing ? null : video.id)}
                            >
                              {isPreviewing && (
                                <div className="border-t border-[var(--editor-border)] bg-black">
                                  <video src={url} controls className="w-full max-h-40 object-contain" />
                                </div>
                              )}
                            </BrowseRow>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* My Pro Library section */}
                  {coachProVideos.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-[var(--editor-text-muted)] uppercase tracking-wider px-1 mb-1.5 mt-2">My Pro Library</p>
                      <div className="space-y-1.5">
                        {coachProVideos.map((video) => {
                          const url = video.webOptimizedVideoUrl ?? video.videoUrl;
                          const isPreviewing = proPreviewing === video.id;
                          /* ProVideo has title, playerName, tags, an optional
                             description and an optional durationSeconds. Title
                             leads; playerName and tags are what separate two
                             videos of the same stroke, so they form the quiet
                             line. Description is a paragraph, not a row. */
                          const secondary = [
                            video.playerName,
                            ...video.tags,
                            video.durationSeconds ? formatTime(video.durationSeconds) : null,
                          ].filter(Boolean).join(' · ');
                          return (
                            <BrowseRow
                              key={video.id}
                              thumb={(
                                <RowThumbnail
                                  cacheKey={`pro:${video.id}`}
                                  thumbnailUrl={video.thumbnailUrl}
                                  videoUrl={url}
                                />
                              )}
                              primary={video.title}
                              secondary={secondary || undefined}
                              selected={selected?.videoUrl === url}
                              onSelect={() => handleSelectFromProLibrary(video)}
                              previewing={isPreviewing}
                              onTogglePreview={() => setProPreviewing(isPreviewing ? null : video.id)}
                            >
                              {isPreviewing && (
                                <div className="border-t border-[var(--editor-border)] bg-black">
                                  <video src={url} controls className="w-full max-h-40 object-contain" />
                                </div>
                              )}
                            </BrowseRow>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Camera */}
          {tab === 'camera' && (
            <div className="space-y-3">
              {cameraError ? (
                <div className="text-sm text-red-500 px-1">{cameraError}</div>
              ) : (
                <>
                  <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-5 h-5 border-2 border-gray-400 border-t-white rounded-full animate-spin" />
                    </div>
                    <video
                      ref={cameraRef}
                      autoPlay
                      muted
                      playsInline
                      className="relative w-full h-full object-contain z-10"
                      style={{ transform: 'scaleX(-1)' }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => commit({ videoUrl: '', isCameraSource: true, label: 'Camera' })}
                    className={`${EBTN_PRIMARY} w-full`}
                  >
                    Use Camera
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        </>
        )}

        {/* One action, and only once there is something to open. A disabled
            button sitting there from the start is a promise the screen cannot
            keep yet. */}
        {selected && (
          <div className="px-5 pb-5 pt-3 flex-shrink-0" style={{ borderTop: '1px solid var(--editor-border)' }}>
            <button type="button" onClick={() => commit(selected)} className={`${EBTN_PRIMARY} w-full`}>
              Open
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
